import React, {createContext, useContext, useMemo} from 'react';

import {
  UseHistoryKeysQueryResult,
  VizMap,
} from '../../state/graphql/historyKeysQuery';
import {
  useKeyInfoQuery,
  useMultiRunsetKeyInfoQuery,
} from '../../state/runs/hooks';
import * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import * as RunSetTypes from '../../state/views/runSet/types';
import {RunHistoryKeyInfo} from '../../types/run';
import * as RunHelpers from '../../util/runhelpers';

export type KeyInfoQueryResult = {
  loading: boolean;
  error: true | null;
  historyKeyInfo: RunHistoryKeyInfo & {sortedKeys: string[]};
  viz: VizMap;
};

type KeyInfoQueryContext = {
  keyInfoQuery: KeyInfoQueryResult;
  uniqueKeyTypes?: Set<string>;
  runSetRefs: RunSetTypes.Ref[];
  customRunNamesRef: CustomRunNamesTypes.Ref | undefined;
};

function createKeyInfoQueryContext() {
  return createContext<KeyInfoQueryContext | undefined>(undefined);
}

function createKeyInfoQueryContextProvider(
  useKeyInfoQueryResult: (
    runSetRefs: RunSetTypes.Ref[],
    customRunNamesRef: CustomRunNamesTypes.Ref | undefined
  ) => KeyInfoQueryResult,
  Context: React.Context<KeyInfoQueryContext | undefined>
) {
  return function KeyInfoQueryContextProvider({
    children,
    runSetRefs,
    customRunNamesRef,
  }: {
    children: React.ReactNode;
    runSetRefs: RunSetTypes.Ref[];
    customRunNamesRef: CustomRunNamesTypes.Ref | undefined;
  }) {
    const keyInfoQueryResult = useKeyInfoQueryResult(
      runSetRefs,
      customRunNamesRef
    );
    const uniqueKeyTypes = useMemo(
      () => makeUniqueKeyTypes(keyInfoQueryResult),
      [keyInfoQueryResult]
    );

    const value = useMemo(
      () => ({
        keyInfoQuery: keyInfoQueryResult,
        uniqueKeyTypes,
        runSetRefs,
        customRunNamesRef,
      }),
      [keyInfoQueryResult, uniqueKeyTypes, runSetRefs, customRunNamesRef]
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
  };
}

const KeyInfoQueryContext = createKeyInfoQueryContext();

const KeyInfoQueryContextProviderComponent = createKeyInfoQueryContextProvider(
  (
    runSetRefs: RunSetTypes.Ref[],
    customRunNamesRef: CustomRunNamesTypes.Ref | undefined
  ) => useKeyInfoQueryResult(runSetRefs, customRunNamesRef, useKeyInfoQuery),
  KeyInfoQueryContext
);

const MultiRunSetKeyInfoQueryContextProviderComponent =
  createKeyInfoQueryContextProvider(
    (
      runSetRefs: RunSetTypes.Ref[],
      customRunNamesRef: CustomRunNamesTypes.Ref | undefined
    ) =>
      useKeyInfoQueryResult(
        runSetRefs,
        customRunNamesRef,
        useMultiRunsetKeyInfoQuery
      ),
    KeyInfoQueryContext
  );

export const KeyInfoQueryContextProvider = ({
  children,
  runSetRefs,
  customRunNamesRef,
  allowMultiRunsetHistoryKeys: allowMultiRunsetHistoryKeys = false,
}: {
  children: React.ReactNode;
  runSetRefs: RunSetTypes.Ref[];
  customRunNamesRef?: CustomRunNamesTypes.Ref;
  allowMultiRunsetHistoryKeys?: boolean;
}) => {
  if (allowMultiRunsetHistoryKeys) {
    return (
      <MultiRunSetKeyInfoQueryContextProviderComponent
        runSetRefs={runSetRefs}
        customRunNamesRef={customRunNamesRef}>
        {children}
      </MultiRunSetKeyInfoQueryContextProviderComponent>
    );
  }
  return (
    <KeyInfoQueryContextProviderComponent
      runSetRefs={runSetRefs}
      customRunNamesRef={customRunNamesRef}>
      {children}
    </KeyInfoQueryContextProviderComponent>
  );
};

export const useKeyInfoQueryContext = () => {
  const context = useContext(KeyInfoQueryContext);

  if (!context) {
    throw new Error(
      'useKeyInfoQueryContext must be used within a KeyInfoQueryContextProvider'
    );
  }

  return context;
};

function makeUniqueKeyTypes(
  keyInfoQuery: KeyInfoQueryResult
): Set<string> | undefined {
  if (keyInfoQuery.loading || keyInfoQuery.error != null) {
    return undefined;
  }

  const {historyKeyInfo} = keyInfoQuery;
  if (historyKeyInfo == null) {
    return undefined;
  }

  const keyTypes = RunHelpers.keyTypes(historyKeyInfo.keys);
  if (keyTypes == null) {
    return undefined;
  }
  return new Set<string>(Object.values(keyTypes) as string[]);
}

export const useKeyInfoQueryResult = (
  runSetRefs: RunSetTypes.Ref[],
  customRunNamesRef: CustomRunNamesTypes.Ref | undefined,
  keyInfoQueryFunc: (
    runSetRefs: RunSetTypes.Ref[],
    customRunNamesRef: CustomRunNamesTypes.Ref | undefined
  ) => UseHistoryKeysQueryResult
): KeyInfoQueryResult => {
  const keyInfoQuery = keyInfoQueryFunc(runSetRefs, customRunNamesRef);
  const isKeyInfoLoading = keyInfoQuery.loading;
  const keyInfoQueryError = keyInfoQuery.error;

  const historyKeyInfo = useMemo(() => {
    if (isKeyInfoLoading || keyInfoQueryError != null) {
      return {sets: [], keys: {}, sortedKeys: []};
    }
    return keyInfoQuery.historyKeyInfo;
  }, [isKeyInfoLoading, keyInfoQuery, keyInfoQueryError]);
  const viz = useMemo(() => {
    if (isKeyInfoLoading || keyInfoQueryError != null) {
      return {};
    }
    return keyInfoQuery.viz;
  }, [isKeyInfoLoading, keyInfoQuery, keyInfoQueryError]);

  return useMemo(() => {
    return {
      loading: isKeyInfoLoading,
      error: keyInfoQueryError,
      historyKeyInfo,
      viz,
    };
  }, [historyKeyInfo, viz, isKeyInfoLoading, keyInfoQueryError]);
};
